/* How many shares a lot held on a given day, according to its own trades.
 *
 * A dividend record stores the count held on the pay date, and the screen that
 * writes one offered today's count — the lot as it stands, whatever date was
 * being entered. For a payment recorded the week it landed that is right. For
 * one entered a month later, after buying more of the same company, it is a
 * number that is wrong in the direction of paying you more than you were paid,
 * filled in silently and presented as a fact.
 *
 * The book could not have done better when the screen was written, because
 * there was no trade history to ask. There is one now, and where it accounts
 * for the lot in full, the count on any earlier day is arithmetic rather than
 * memory.
 *
 * ── the test for "can say" ───────────────────────────────────────────────
 *
 * The same one `state/basisFromTrades.ts` applies, and for the same reason: the
 * trades have to add up to what the lot holds today. If they say twenty and the
 * lot says twenty, they are the whole history and any earlier point on them is
 * real. If they say three and the lot says twenty, seventeen shares arrived
 * some other way, at some unknown date, and a count read off those three would
 * be a confident answer about a holding they know almost nothing about.
 *
 * Refusing is not a failure here. It hands the field back to the person who
 * knows, which is where it was before, and the screen says which of the two
 * happened. */

import type { Holding, Trade } from '../types';

export type SharesOnRefusal = 'no-trades' | 'shares-differ';

export interface SharesOnDate {
  /** Held at the end of that day, or null when the trades cannot say. */
  shares: number | null;
  /** What the trades account for as of today, whatever the lot says. */
  netShares: number;
  /** What the lot holds now — the fallback, and the figure worth naming when
      the answer is a refusal. */
  heldNow: number;
  why?: SharesOnRefusal;
}

/** Fractional holdings are ordinary and floating point does not add cleanly. */
const CLOSE_ENOUGH = 1e-6;

/** Trades on this lot, oldest first. */
function tradesFor(trades: Trade[], holding: Holding): Trade[] {
  return trades
    .filter((t) => t.ticker === holding.ticker && t.account === holding.account)
    .sort((a, b) => a.at.localeCompare(b.at));
}

/** Shares after replaying trades up to and including `throughDate`, or all of
    them when it is undefined. */
function replay(mine: Trade[], throughDate?: string): number {
  let shares = 0;
  for (const t of mine) {
    /* Compared as written dates rather than as instants. A trade stamped 00:05
       and a pay date are both records of a day, and converting either through a
       zone would move it to the day before for anyone east of the recording —
       the same rule the cash-flow import keeps for the same reason. */
    if (throughDate !== undefined && t.at.slice(0, 10) > throughDate) break;
    const size = Math.abs(t.shares);
    if (!(size > 0)) continue;
    shares = t.side === 'buy' ? shares + size : Math.max(0, shares - size);
  }
  return shares;
}

/** What this lot held at the end of `date` (an ISO day, `YYYY-MM-DD`). */
export function sharesOn(trades: Trade[], holding: Holding, date: string): SharesOnDate {
  const mine = tradesFor(trades, holding);
  const heldNow = holding.shares;

  if (mine.length === 0) {
    return { shares: null, netShares: 0, heldNow, why: 'no-trades' };
  }

  const netShares = replay(mine);
  if (Math.abs(netShares - heldNow) > CLOSE_ENOUGH) {
    return { shares: null, netShares, heldNow, why: 'shares-differ' };
  }

  /* Zero is an answer, not a refusal: the trades are complete and they say the
     shares had not been bought yet. A date typed a year early gets told so,
     which is more use than a count quietly carried over from today. */
  return { shares: replay(mine, date), netShares, heldNow };
}
